import React, { memo } from "react";
import { ArrowDownRight, ArrowUpRight, CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import { isNum, num, pct, pts, rupees } from "./fmt";

/*
 * CALL vs PUT — what each side costs and whether today's move can pay for it
 * (2026-10-06 rework of the 2026-10-05 split).
 *
 * Left half CALL, right half PUT. The HEADLINE of each half is the cost check
 * (option price, one lot, points needed to cover costs / reach the target,
 * expected move left, theta share) drawn as a bar: can the expected move reach
 * the target marker? That is the arithmetic that decides a buyer's profit.
 *
 * The direction share (% of signals on each side) is kept as the thin bar on
 * top and the proof list folds away: in this system's own tests direction
 * signals were right about 50-55% of the time, so they are context, not the
 * decision. Breadth was dropped from the vote — it is the index's own price
 * read a second time.
 *
 * Pure presentation over payloads the Dashboard already holds.
 */

const n = (v) => (isNum(v) ? Number(v) : null);
const sideOf = (x, band = 0) => (x > band ? "CALL" : x < -band ? "PUT" : "NEUTRAL");

const TREND_VOTES = {
  daymove: "Move since today's open",
  prevclose: "Move since yesterday's close",
  orb: "Break of the first 30 minutes' range",
  er24: "Last 2 hours: clean one-way move",
  di: "Buying vs selling pressure (DI)",
};
const strength = (v) => (Math.abs(v) >= 0.6 ? "strong" : Math.abs(v) >= 0.2 ? "mild" : "flat");

/* Every signal as { key, group, label, side: CALL | PUT | NEUTRAL, proof }.
 * A signal with no data is left out, never counted as neutral. */
export function buildEvidence({ view, optionData, trend } = {}) {
  const out = [];
  const add = (key, group, label, side, proof) => out.push({ key, group, label, side, proof });
  const t = view?.technical || {};
  const spot = n(view?.spot) || n(t.close);

  const vwap = n(t.vwap);
  if (spot && vwap) {
    const d = spot - vwap;
    add("vwap", "Price", "Price vs today's average (VWAP)", sideOf(d, vwap * 0.0005),
      `${num(spot, 0)} is ${num(Math.abs(d), 0)} pts ${d >= 0 ? "above" : "below"} the average ${num(vwap, 0)}`);
  }
  const e9 = n(t.ema_9), e21 = n(t.ema_21);
  if (e9 && e21) {
    add("ema", "Price", "Short trend (EMA 9 vs EMA 21)", sideOf(e9 - e21, e21 * 0.0001),
      `fast average ${num(e9, 0)} is ${e9 >= e21 ? "above" : "below"} slow average ${num(e21, 0)}`);
  }
  const rsi = n(t.rsi_14);
  if (rsi != null && t.available !== false && t.rsi_14 != null) {
    add("rsi", "Price", "Momentum (RSI)", rsi >= 55 ? "CALL" : rsi <= 45 ? "PUT" : "NEUTRAL",
      `RSI ${num(rsi, 0)} — above 55 favours calls, below 45 favours puts`);
  }
  if (t.supertrend_direction === "BULLISH" || t.supertrend_direction === "BEARISH") {
    const up = t.supertrend_direction === "BULLISH";
    add("supertrend", "Price", "Supertrend", up ? "CALL" : "PUT",
      `price is ${up ? "above" : "below"} the supertrend line${isNum(t.supertrend) ? ` ${num(t.supertrend, 0)}` : ""}`);
  }

  if (trend?.available) {
    add("regime", "Trend", "Market regime", trend.state === "UP" ? "CALL" : trend.state === "DOWN" ? "PUT" : "NEUTRAL",
      `${trend.state === "SIDEWAYS" ? "sideways" : `trending ${String(trend.state).toLowerCase()}`}${isNum(trend.minutes_in_state) ? ` for ${num(trend.minutes_in_state)} min` : ""}`);
    (trend.votes || []).forEach((v) => {
      const label = TREND_VOTES[v.key];
      const val = n(v.value);
      if (!label || val == null) return;
      add(v.key, "Trend", label, sideOf(val, 0.2), `${strength(val)}${strength(val) === "flat" ? "" : val > 0 ? " up" : " down"} (${val > 0 ? "+" : ""}${num(val, 2)} on a −1 to +1 scale)`);
    });
  }

  const oiScore = n(optionData?.score);
  if (optionData?.available !== false && oiScore != null) {
    add("oi", "Options", "Open-interest flow", oiScore >= 58 ? "CALL" : oiScore <= 42 ? "PUT" : "NEUTRAL",
      `OI score ${num(oiScore, 0)} of 100 — above 58 favours calls, below 42 favours puts`);
  }
  const ce = n(optionData?.premium_momentum?.ce_pct), pe = n(optionData?.premium_momentum?.pe_pct);
  if (ce != null && pe != null && (ce !== 0 || pe !== 0)) {
    add("premium", "Options", "Which option's price is rising", sideOf(ce - pe, 0.35),
      `call price ${ce >= 0 ? "+" : ""}${num(ce, 1)}% · put price ${pe >= 0 ? "+" : ""}${num(pe, 1)}%`);
  }
  const cs = n(optionData?.call_strength), ps = n(optionData?.put_strength);
  if (cs != null && ps != null) {
    add("strength", "Options", "Call strength vs put strength", sideOf(cs - ps, 8),
      `calls ${num(cs, 0)} · puts ${num(ps, 0)} (each of 100)`);
  }
  return out;
}

/* Share of the signals on each side; a neutral signal gives half to each. */
export function callPutShare(evidence) {
  const total = evidence.length;
  const call = evidence.filter((e) => e.side === "CALL").length;
  const put = evidence.filter((e) => e.side === "PUT").length;
  const neutral = total - call - put;
  if (!total) return { total: 0, call, put, neutral, callPct: null, putPct: null };
  const callPct = Math.round(((call + neutral / 2) / total) * 100);
  return { total, call, put, neutral, callPct, putPct: 100 - callPct };
}

/* Can today's expected move pay for this option? Plain rows from the buyer-edge payload. */
export function costCheck(side, edge) {
  if (!side?.available) return null;
  const em = n(edge?.expected_move?.blend_rest);
  const be = n(side.breakeven_spot_move), need = n(side.required_spot_move);
  const verdict = side.can_profit === true ? { word: "Move can reach the target", tone: "emerald" }
    : side.can_breakeven === true ? { word: "Move only covers costs", tone: "amber" }
      : { word: "Move too small to cover costs", tone: "rose" };
  const theta = isNum(side.theta_share_of_gain) ? Number(side.theta_share_of_gain) * 100 : null;
  return {
    premium: side.premium, lotCost: side.lot_cost_rupees, costPerLot: side.cost_rupees_per_lot, delta: side.delta,
    em, be, need, verdict, theta, targetPct: edge?.target_pct,
  };
}

/* Bar geometry: fill = expected move; markers at breakeven and target. */
export function coverage(c) {
  if (!c || c.em == null || c.need == null) return null;
  const max = Math.max(c.em, c.need) * 1.15 || 1;
  const p = (x) => Math.max(0, Math.min(100, (x / max) * 100));
  return { fill: p(c.em), be: c.be == null ? null : p(c.be), target: p(c.need) };
}

const SIDE = {
  CALL: { ink: "text-emerald-300", shell: "border-emerald-500/40 bg-emerald-500/[.05]", Icon: ArrowUpRight, name: "CALL", sub: "pays when the market goes up" },
  PUT: { ink: "text-rose-300", shell: "border-rose-500/40 bg-rose-500/[.05]", Icon: ArrowDownRight, name: "PUT", sub: "pays when the market goes down" },
};
const VERDICT = {
  emerald: { ink: "text-emerald-300", bar: "bg-emerald-400", Icon: CheckCircle2 },
  amber: { ink: "text-amber-300", bar: "bg-amber-400", Icon: MinusCircle },
  rose: { ink: "text-rose-300", bar: "bg-rose-400", Icon: XCircle },
};

function CoverageBar({ c }) {
  const g = coverage(c);
  if (!g) return null;
  const v = VERDICT[c.verdict.tone];
  return (
    <div className="mt-2.5" title={`expected ±${num(c.em, 0)} pts · costs ${pts(c.be)} · target ${pts(c.need)}`}>
      <div className="relative h-3 w-full rounded-full bg-slate-800">
        <div className={`h-3 rounded-full transition-[width] duration-700 ${v.bar}`} style={{ width: `${g.fill}%` }} />
        {g.be != null && <span className="absolute -top-1 h-5 w-0.5 bg-amber-300" style={{ left: `${g.be}%` }} />}
        <span className="absolute -top-1 h-5 w-0.5 bg-emerald-300" style={{ left: `${g.target}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-500">
        <span>expected move <b className="text-hi">±{num(c.em, 0)}</b></span>
        <span className="text-amber-300">costs {pts(c.be)}</span>
        <span className="text-emerald-300">target {pts(c.need)}</span>
      </div>
    </div>
  );
}

function CostCard({ c, closed }) {
  if (!c) return <p className="rounded-lg border border-line bg-surface px-3 py-2.5 text-sm text-slate-500">No live option price yet.</p>;
  const v = VERDICT[c.verdict.tone];
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2.5 text-sm text-slate-300">
      <p className={`flex items-center gap-1.5 text-base font-black ${v.ink}`}><v.Icon className="h-5 w-5" />{c.verdict.word}{closed ? " (last snapshot)" : ""}</p>
      <CoverageBar c={c} />
      <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1 tabular-nums">
        <span>ATM price <b className="text-hi">{rupees(c.premium, 1)}</b>{isNum(c.delta) && <span className="text-slate-500"> · Δ {num(c.delta, 2)}</span>}</span>
        <span>One lot costs <b className="text-hi">{rupees(c.lotCost)}</b></span>
        <span>Target <b className="text-hi">+{pct(c.targetPct, 0)}</b> on the premium</span>
        <span>Charges <b className="text-hi">{rupees(c.costPerLot)}</b> per lot</span>
        {c.theta != null && <span className="col-span-2">Time decay eats <b className={c.theta > 50 ? "text-amber-300" : "text-hi"}>{pct(c.theta, 0)}</b> of the expected gain by square-off</span>}
      </div>
    </div>
  );
}

function Half({ sideKey, share, items, cost, closed }) {
  const s = SIDE[sideKey];
  const value = sideKey === "CALL" ? share.callPct : share.putPct;
  const count = sideKey === "CALL" ? share.call : share.put;
  return (
    <div data-testid={`split-${sideKey.toLowerCase()}`} className={`flex min-w-0 flex-col gap-3 rounded-2xl border p-4 ${s.shell}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <s.Icon className={`h-8 w-8 ${s.ink}`} strokeWidth={2.5} />
          <div>
            <p className={`text-2xl font-black leading-7 ${s.ink}`}>{s.name}</p>
            <p className="text-[12px] text-slate-400">{s.sub}</p>
          </div>
        </div>
        <div className="text-right" title="share of direction signals on this side — not a win probability">
          <p className={`text-3xl font-black leading-none tabular-nums ${s.ink}`}>{value == null ? "—" : `${value}%`}</p>
          <p className="mt-1 text-[11px] text-slate-400">{share.total ? `${count} of ${share.total} signals` : "no signals yet"}</p>
        </div>
      </div>
      <CostCard c={cost} closed={closed} />
      <details className="group rounded-lg border border-line bg-surface/60">
        <summary className="cursor-pointer select-none px-3 py-2 text-[12px] font-bold text-slate-400 hover:text-hi">
          Why {count} signal{count === 1 ? "" : "s"} favour{count === 1 ? "s" : ""} {sideKey === "CALL" ? "calls" : "puts"}
        </summary>
        <ul className="space-y-1.5 border-t border-line px-3 py-2">
          {items.length === 0 && <li className="text-[12px] text-slate-500">No signal favours {sideKey === "CALL" ? "calls" : "puts"} right now.</li>}
          {items.map((e) => (
            <li key={e.key} className="flex items-start gap-2 text-[12px]">
              <CheckCircle2 className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${s.ink}`} />
              <span className="min-w-0"><b className="text-hi">{e.label}</b> <span className="text-slate-400">— {e.proof}</span></span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function CallPutSplit({ view, optionData, trend, edge }) {
  const evidence = buildEvidence({ view, optionData, trend });
  const share = callPutShare(evidence);
  const closed = Boolean(view?.sessionClosed);
  return (
    <section data-testid="call-put-split" className="space-y-3">
      {share.total > 0 && (
        <div data-testid="split-share">
          <div className="flex h-3 overflow-hidden rounded-full bg-slate-800" title={`${share.call} call · ${share.neutral} neutral · ${share.put} put`}>
            <div className="bg-emerald-400 transition-[width] duration-700" style={{ width: `${share.callPct}%` }} />
            <div className="bg-rose-400 transition-[width] duration-700" style={{ width: `${share.putPct}%` }} />
          </div>
          <div className="mt-1 flex justify-between gap-2 text-[12px] font-bold">
            <span className="text-emerald-300">CALL {share.callPct}%</span>
            <span className="hidden text-slate-500 sm:inline">direction signals · context only (right ~50–55% of the time in tests)</span>
            <span className="text-rose-300">PUT {share.putPct}%</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Half sideKey="CALL" share={share} items={evidence.filter((e) => e.side === "CALL")} cost={costCheck(edge?.call, edge)} closed={closed} />
        <Half sideKey="PUT" share={share} items={evidence.filter((e) => e.side === "PUT")} cost={costCheck(edge?.put, edge)} closed={closed} />
      </div>
    </section>
  );
}

export default memo(CallPutSplit);
